import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Send, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useTranslation } from "react-i18next";

export default function RequestAccess() {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  useSEO({
    title: "Demande d'accès · Noam",
    description: "Demandez un accès à la plateforme Noam. Onboarding sur mesure pour comités exécutifs et investisseurs.",
    path: "/request-access",
  });
  const [form, setForm] = useState({ nom: "", organisation: "", email: "", contexte: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.nom.trim() || !form.email.trim() || !form.organisation.trim()) {
      setError("Veuillez remplir nom, organisation et email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/access-requests", form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Envoi impossible. Réessayez ou contactez-nous.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="request-access-done">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-[#1D9E75]" />
          </div>
          <h1 className="noam-display text-2xl font-semibold mt-5 tracking-tight">{en ? "Request received." : "Demande reçue."}</h1>
          <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
            {en ? "Constante Partners is reviewing your request. We will contact you within 48 business hours from Geneva." : "Constante Partners examine votre demande. Nous vous contacterons sous 48 heures ouvrées depuis Genève."}
          </p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white mt-8" data-testid="request-back-home">
            <ArrowLeft className="w-3 h-3" /> {en ? "Back to home" : "Retour à l'accueil"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="request-access-root">
      <div className="w-full max-w-lg">
        <div className="noam-overline text-[#EF9F27]">{en ? "Private access · Constante Partners" : "Accès privé · Constante Partners"}</div>
        <h1 className="noam-display text-3xl font-semibold tracking-tight mt-2">{en ? "Request access." : "Demander un accès."}</h1>
        <p className="text-zinc-400 text-sm mt-3 max-w-md leading-relaxed">
          {en ? "Access to Noam is restricted and manually validated. Introduce yourself in a few words. A Constante Partners partner will reply." : "L'accès à Noam est restreint et validé manuellement. Présentez-vous en quelques mots. Un partenaire de Constante Partners vous répondra."}
        </p>

        <form onSubmit={submit} className="space-y-4 mt-8">
          <Field label={en ? "Name" : "Nom"} required>
            <input
              autoFocus
              value={form.nom}
              onChange={update("nom")}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-[#EF9F27] focus:outline-none"
              placeholder={en ? "First and last name" : "Prénom et nom"}
              data-testid="request-nom"
            />
          </Field>
          <Field label={en ? "Organization" : "Organisation"} required>
            <input
              value={form.organisation}
              onChange={update("organisation")}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-[#EF9F27] focus:outline-none"
              placeholder={en ? "Private bank, family office, fund, etc." : "Banque privée, family office, fonds, etc."}
              data-testid="request-organisation"
            />
          </Field>
          <Field label={en ? "Business email" : "Email professionnel"} required>
            <input
              type="email"
              value={form.email}
              onChange={update("email")}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-[#EF9F27] focus:outline-none"
              placeholder="prenom.nom@institution.ch"
              data-testid="request-email"
            />
          </Field>
          <Field label={en ? "Context (optional)" : "Contexte (optionnel)"}>
            <textarea
              value={form.contexte}
              onChange={update("contexte")}
              rows={4}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-[#EF9F27] focus:outline-none resize-none"
              placeholder={en ? "A few words about your intent. Keep it brief." : "Quelques mots sur votre intention. Restez bref."}
              data-testid="request-contexte"
            />
          </Field>

          {error && (
            <div className="flex items-start gap-2 text-[#FCA5A5] text-xs" data-testid="request-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white" data-testid="request-back">
              <ArrowLeft className="w-3 h-3" /> {en ? "Back" : "Retour"}
            </Link>
            <button type="submit" disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="request-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {en ? "Send the request" : "Envoyer la demande"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-400 mb-1.5 inline-block">
        {label} {required && <span className="text-[#EF9F27]">*</span>}
      </span>
      {children}
    </label>
  );
}
